// Admin tab: manage recurrency time slots and link to recurrency form / flyer data.
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { firestore } from '../firebase';
import { collection, getDocs, doc, updateDoc, query, where, serverTimestamp, deleteField } from 'firebase/firestore';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { getFreshlingDisplayName } from '../utils/flyerDisplay';
import { formatHeight, formatWeight } from '../utils/heightWeightUtils';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import './RecurrencyAdmin.css';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const RECURRENCY_EXPORT_COLS = [
  { id: 'name', label: 'Name', default: true },
  { id: 'email', label: 'Email', default: false },
  { id: 'phone', label: 'Phone', default: true },
  { id: 'rec', label: 'Rec', default: true },
  { id: 'responded', label: 'Responded?', default: true },
  { id: 'form', label: 'Form?', default: false },
  { id: 'reminder', label: 'Reminder?', default: false },
  { id: 'lastJump', label: 'Last jump', default: true },
  { id: 'wedAm', label: 'Wed AM?', default: true },
  { id: 'notes', label: 'Notes', default: true },
  { id: 'slot', label: 'Slot', default: true },
  { id: 'jumps', label: 'Jumps', default: true },
  { id: 'homeDz', label: 'Home DZ', default: true },
  { id: 'ht', label: 'Ht', default: false },
  { id: 'wt', label: 'Wt', default: false },
];

const RecurrencyAdmin = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [slots, setSlots] = useState([]);
  const [freshlingsYesUnsure, setFreshlingsYesUnsure] = useState([]);
  const [freshlingsLoading, setFreshlingsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [markingSentId, setMarkingSentId] = useState(null);
  const [markingReminderId, setMarkingReminderId] = useState(null);
  const [showSlotsEditor, setShowSlotsEditor] = useState(false);
  const { emailComposerProps, openEmailComposer, closeEmailComposer } = useEmailComposer();
  const initialRecurrencyByFlyerId = useRef({});
  const [recurrencyTableExport, setRecurrencyTableExport] = useState(null); // { mode } | null
  const [recurrencyExportCols, setRecurrencyExportCols] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [qs, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length > 0) {
          setSelectedEventId((prev) =>
            prev && list.some((e) => e.id === prev)
              ? prev
              : resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: list })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ev = events.find(e => e.id === selectedEventId) || null;
    setSelectedEvent(ev);
    setSlots(Array.isArray(ev?.recurrencyTimeSlots) ? ev.recurrencyTimeSlots : []);
  }, [selectedEventId, events]);

  // Fetch freshlings (Yes/Unsure) for selected event
  useEffect(() => {
    if (!selectedEventId) {
      setFreshlingsYesUnsure([]);
      return;
    }
    setFreshlingsLoading(true);
    (async () => {
      try {
        const q = query(
          collection(firestore, 'flyers'),
          where('eventId', '==', selectedEventId),
          where('type', '==', 'freshling')
        );
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const filtered = list.filter(
          f => !f.cancelled && (f.recurrency === 'Yes' || f.recurrency === 'Unsure')
        );
        initialRecurrencyByFlyerId.current = {};
        filtered.forEach((f) => {
          initialRecurrencyByFlyerId.current[f.id] = f.recurrency ?? null;
        });
        setFreshlingsYesUnsure(filtered);
      } catch (err) {
        setError('Failed to load freshlings: ' + err.message);
        setFreshlingsYesUnsure([]);
      } finally {
        setFreshlingsLoading(false);
      }
    })();
  }, [selectedEventId]);

  const handleSlotChange = (idx, field, value) => {
    const next = slots.map((s, i) =>
      i === idx ? { ...s, [field]: value } : s
    );
    setSlots(next);
  };

  const handleRemoveSlot = (idx) => {
    setSlots(slots.filter((_, i) => i !== idx));
  };

  const handleAddSlot = () => {
    setSlots([
      ...slots,
      {
        id: typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `slot-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        start: '09:00',
        checkIn: '',
        maxParticipants: 8,
      },
    ]);
  };

  const handleSave = async () => {
    if (!selectedEventId) return;
    setError('');
    setSaving(true);
    try {
      await updateDoc(doc(firestore, 'events', selectedEventId), {
        recurrencyTimeSlots: slots,
      });
      setEvents(events.map(ev =>
        ev.id === selectedEventId ? { ...ev, recurrencyTimeSlots: slots } : ev
      ));
    } catch (err) {
      setError('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getSlotLabel = useCallback(
    (timeSlotId) => {
      const slot = (selectedEvent?.recurrencyTimeSlots || []).find((s) => s.id === timeSlotId);
      if (!slot) return timeSlotId || '';
      if (slot.checkIn && slot.start) return `Check in ${slot.checkIn}, ${slot.start} class begins`;
      return slot.label || slot.start || timeSlotId;
    },
    [selectedEvent?.recurrencyTimeSlots]
  );

  /** Format date as "May 2, 2025". Handles ISO string or Firestore Timestamp. */
  const formatLastJumpDate = useCallback((val) => {
    if (val == null || val === '') return '—';
    const d = val?.toDate ? val.toDate() : typeof val === 'string' ? new Date(val) : val;
    if (!(d instanceof Date) || isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }, []);

  const handleRecurrencyChange = async (flyerId, value) => {
    const val = value === '' ? null : value;
    setError('');
    try {
      await updateDoc(doc(firestore, 'flyers', flyerId), { recurrency: val });
      setFreshlingsYesUnsure((prev) => {
        if (val === 'Yes' || val === 'Unsure') {
          return prev.map((f) => (f.id === flyerId ? { ...f, recurrency: val } : f));
        }
        return prev.filter((f) => f.id !== flyerId);
      });
    } catch (err) {
      setError('Error updating recurrency: ' + err.message);
    }
  };

  const clearFormData = async (flyer) => {
    setError('');
    const originalRecurrency = initialRecurrencyByFlyerId.current[flyer.id] ?? flyer.recurrency ?? null;
    try {
      await updateDoc(doc(firestore, 'flyers', flyer.id), {
        recurrencyUpdate: deleteField(),
        recurrency: originalRecurrency,
      });
      setFreshlingsYesUnsure((prev) =>
        prev.map((f) =>
          f.id === flyer.id
            ? { ...f, recurrencyUpdate: undefined, recurrency: originalRecurrency }
            : f
        )
      );
    } catch (err) {
      setError('Failed to clear form data: ' + err.message);
    }
  };

  const toggleRecurrencyEmailManual = async (flyer) => {
    const flyerId = flyer.id;
    const current = !!flyer.recurrencyFormEmailManual;
    setError('');
    setMarkingSentId(flyerId);
    try {
      const updates = current
        ? { recurrencyFormEmailManual: false, recurrencyFormEmailSentAt: null }
        : { recurrencyFormEmailManual: true };
      await updateDoc(doc(firestore, 'flyers', flyerId), updates);
      setFreshlingsYesUnsure((prev) =>
        prev.map((f) =>
          f.id === flyerId ? { ...f, ...updates } : f
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update recurrency email status.');
    } finally {
      setMarkingSentId(null);
    }
  };

  const toggleRecurrencyReminderManual = async (flyer) => {
    const flyerId = flyer.id;
    const current = !!flyer.recurrencyReminderManual;
    setError('');
    setMarkingReminderId(flyerId);
    try {
      const updates = current
        ? { recurrencyReminderManual: false, recurrencyReminderSentAt: null }
        : { recurrencyReminderManual: true };
      await updateDoc(doc(firestore, 'flyers', flyerId), updates);
      setFreshlingsYesUnsure((prev) =>
        prev.map((f) =>
          f.id === flyerId ? { ...f, ...updates } : f
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update reminder status.');
    } finally {
      setMarkingReminderId(null);
    }
  };

  const yesCount = freshlingsYesUnsure.filter(f => f.recurrency === 'Yes').length;
  const unsureCount = freshlingsYesUnsure.filter(f => f.recurrency === 'Unsure').length;

  /** Grouped for print: by time slot (class) in slot order, then Non-responders. */
  const printGrouped = useMemo(() => {
    const slots = selectedEvent?.recurrencyTimeSlots || [];
    const slotLabel = (sid) => {
      const slot = (selectedEvent?.recurrencyTimeSlots || []).find(s => s.id === sid);
      if (!slot) return sid || '';
      if (slot.checkIn && slot.start) return `Check in ${slot.checkIn}, ${slot.start} class begins`;
      return slot.label || slot.start || sid;
    };
    const bySlot = new Map();
    slots.forEach((slot) => bySlot.set(slot.id, { label: slotLabel(slot.id), flyers: [] }));
    const nonResponders = [];
    freshlingsYesUnsure.forEach((f) => {
      const ru = f.recurrencyUpdate;
      const responded = !!ru && (ru.submittedAt != null || ru.value != null);
      const slotId = ru?.timeSlotId;
      if (responded && slotId && bySlot.has(slotId)) {
        bySlot.get(slotId).flyers.push(f);
      } else {
        nonResponders.push(f);
      }
    });
    const sections = slots.map((slot) => ({ type: 'class', label: slotLabel(slot.id), slotId: slot.id, flyers: bySlot.get(slot.id)?.flyers || [] }));
    if (nonResponders.length) sections.push({ type: 'non-responders', label: 'Non-responders', slotId: null, flyers: nonResponders });
    return sections;
  }, [freshlingsYesUnsure, selectedEvent?.recurrencyTimeSlots]);

  const recurrencyExportRows = useMemo(
    () =>
      freshlingsYesUnsure.map((f) => {
        const ru = f.recurrencyUpdate;
        const responded = !!ru;
        const sentAt = f.recurrencyFormEmailSentAt;
        const manual = !!f.recurrencyFormEmailManual;
        const formSent = !!sentAt || manual;
        const reminderSentAt = f.recurrencyReminderSentAt;
        const reminderManual = !!f.recurrencyReminderManual;
        const reminderSent = !!reminderSentAt || reminderManual;
        return {
          name: getFreshlingDisplayName(f) || f.name || f.email || '—',
          email: (f.email || '').trim(),
          phone: f.phone || '—',
          rec: f.recurrency ?? '—',
          responded: responded ? 'Yes' : 'No',
          form: formSent ? 'Yes' : 'No',
          reminder: reminderSent ? 'Yes' : 'No',
          lastJump: formatLastJumpDate(ru?.lastJumpDate),
          wedAm: ru?.availableWednesdayMorning || '—',
          notes: ru?.notes ? String(ru.notes).replace(/\r?\n/g, ' ') : '—',
          slot: ru?.timeSlotId ? getSlotLabel(ru.timeSlotId) : '—',
          jumps: f.jumpNumber != null && f.jumpNumber !== '' ? String(f.jumpNumber) : '—',
          homeDz: f.homeDz || '—',
          ht: f.ht != null && f.ht !== '' ? formatHeight(String(f.ht)) : '—',
          wt: f.wt != null && f.wt !== '' ? formatWeight(String(f.wt)) : '—',
        };
      }),
    [freshlingsYesUnsure, getSlotLabel, formatLastJumpDate]
  );

  useEffect(() => {
    if (!recurrencyTableExport) return;
    setRecurrencyExportCols(
      new Set(RECURRENCY_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  }, [recurrencyTableExport]);

  const openRecurrencyTableExport = (mode) => {
    if (!recurrencyExportRows.length) {
      window.alert('No freshlings to export for this event.');
      return;
    }
    setRecurrencyTableExport({ mode });
  };

  const resetRecurrencyExportCols = () => {
    setRecurrencyExportCols(
      new Set(RECURRENCY_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runRecurrencyTableExport = (selectedColumnIds) => {
    if (!recurrencyTableExport) return;
    const { mode } = recurrencyTableExport;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : recurrencyExportCols;
    const active = RECURRENCY_EXPORT_COLS.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = recurrencyExportRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const evTitle = selectedEvent?.title || selectedEventId || 'Recurrencies';
    const sub = 'Yes & Unsure freshlings';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} row(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setRecurrencyTableExport(null);
  };

  /** Freshlings who have a scheduled class (chose a time slot). */
  const freshlingsWithSlot = useMemo(
    () =>
      freshlingsYesUnsure.filter(
        (f) => f.recurrencyUpdate && f.recurrencyUpdate.timeSlotId
      ),
    [freshlingsYesUnsure]
  );

  /** Freshlings who have not submitted the recurrency form. */
  const freshlingsNotResponded = useMemo(
    () => freshlingsYesUnsure.filter((f) => !f.recurrencyUpdate),
    [freshlingsYesUnsure]
  );

  const handleEmailSent = async (payload) => {
    const templateKey = payload.selectedTemplate || payload.templateKey;
    const isRecurrency = templateKey === 'recurrency_update' || templateKey === 'recurrency_class_reminder';
    if (!isRecurrency) return;
    const emailContext = payload.context;
    if (!emailContext) return;
    try {
      if (emailContext.mode === 'reminder' && Array.isArray(emailContext.freshlingIds) && emailContext.freshlingIds.length) {
        const ids = emailContext.freshlingIds;
        for (const flyerId of ids) {
          await updateDoc(doc(firestore, 'flyers', flyerId), {
            recurrencyReminderSentAt: serverTimestamp(),
          });
        }
        setFreshlingsYesUnsure((prev) =>
          prev.map((f) =>
            ids.includes(f.id) ? { ...f, recurrencyReminderSentAt: new Date() } : f
          )
        );
      } else if (emailContext.mode === 'bulk' && Array.isArray(emailContext.freshlingIds) && emailContext.freshlingIds.length) {
        const ids = emailContext.freshlingIds;
        // Update Firestore for all selected freshlings
        for (const flyerId of ids) {
          await updateDoc(doc(firestore, 'flyers', flyerId), {
            recurrencyFormEmailSentAt: serverTimestamp(),
          });
        }
        // Update local state
        setFreshlingsYesUnsure((prev) =>
          prev.map((f) =>
            ids.includes(f.id) ? { ...f, recurrencyFormEmailSentAt: new Date() } : f
          )
        );
      } else if (emailContext.mode === 'single' && emailContext.flyerId) {
        const flyerId = emailContext.flyerId;
        const isReminder = emailContext.isReminder === true;
        await updateDoc(doc(firestore, 'flyers', flyerId), {
          [isReminder ? 'recurrencyReminderSentAt' : 'recurrencyFormEmailSentAt']: serverTimestamp(),
        });
        setFreshlingsYesUnsure((prev) =>
          prev.map((f) =>
            f.id === flyerId
              ? { ...f, [isReminder ? 'recurrencyReminderSentAt' : 'recurrencyFormEmailSentAt']: new Date() }
              : f
          )
        );
      }
    } catch (err) {
      setError(err.message || 'Failed to update flyers after email.');
    }
  };

  return (
    <div className="recurrency-admin">
      <h2>Recurrencies</h2>
      <p className="recurrency-admin-intro">
        Configure time slots for the recurrency follow-up form and view submissions. <strong>Rec</strong> is from the import until the participant submits the form or you change it here (form or manual edit overrides import). You can also edit recurrency in <Link to="/admin/flyer-management">Flyers</Link>.
      </p>

      {error && <p className="recurrency-admin-error">{error}</p>}

      <div className="recurrency-admin-event-select">
        <label htmlFor="ru-event">Event:</label>
        <select
          id="ru-event"
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
        >
          {events.map(ev => (
            <option key={ev.id} value={ev.id}>
              {ev.title || ev.id} {ev.isActive ? '(active)' : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedEvent && (
        <>
          <section className="recurrency-admin-section">
            <div className="recurrency-admin-slots-header">
              <h3>Recurrency classes (time slots)</h3>
              <button
                type="button"
                className="recurrency-admin-slots-toggle"
                onClick={() => setShowSlotsEditor((prev) => !prev)}
              >
                {showSlotsEditor ? 'Hide editor' : 'Show editor'}
              </button>
            </div>
            {showSlotsEditor && (
              <>
                <p className="recurrency-admin-hint">
                  These slots are shown on the public recurrency form. Set check-in time (optional), class start time, and max participants. Displayed as &quot;Check in 7:00, 8:00 class begins&quot; when check-in is set.
                </p>
                <div className="recurrency-admin-slots">
                  {slots.map((slot, idx) => (
                    <div key={slot.id || idx} className="recurrency-admin-slot-row">
                      <input
                        type="text"
                        placeholder="Check-in (e.g. 07:00)"
                        value={slot.checkIn || ''}
                        onChange={(e) => handleSlotChange(idx, 'checkIn', e.target.value)}
                        className="recurrency-admin-slot-checkin"
                      />
                      <input
                        type="text"
                        placeholder="Start (e.g. 09:00)"
                        value={slot.start || ''}
                        onChange={(e) => handleSlotChange(idx, 'start', e.target.value)}
                        className="recurrency-admin-slot-start"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        min={1}
                        value={slot.maxParticipants ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          handleSlotChange(idx, 'maxParticipants', v === '' ? undefined : parseInt(v, 10));
                        }}
                        className="recurrency-admin-slot-max"
                      />
                      <button type="button" onClick={() => handleRemoveSlot(idx)} className="recurrency-admin-slot-remove">
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={handleAddSlot} className="recurrency-admin-add-slot">
                  Add class
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="recurrency-admin-save"
                >
                  {saving ? 'Saving…' : 'Save time slots'}
                </button>
              </>
            )}
          </section>

          <section className="recurrency-admin-section recurrency-admin-list-section" id="recurrency-print-area">
            <h3>Yes &amp; Unsure freshlings</h3>
            <p className="recurrency-admin-print-title" aria-hidden="true">
              {selectedEvent?.title || selectedEventId} — Recurrencies
            </p>
            <p className="recurrency-admin-counts">
              <strong>Yes:</strong> {yesCount} &nbsp; <strong>Unsure:</strong> {unsureCount}
            </p>
            {freshlingsLoading ? (
              <p>Loading…</p>
            ) : freshlingsYesUnsure.length === 0 ? (
              <p className="recurrency-admin-muted">No freshlings with recurrency Yes or Unsure for this event.</p>
            ) : (
              <>
                <div className="recurrency-admin-print-bar">
                  <button
                    type="button"
                    onClick={() => {
                      if (!freshlingsYesUnsure.length) return;
                      const emails = freshlingsYesUnsure
                        .map((f) => (f.email || '').trim())
                        .filter(Boolean);
                      if (!emails.length) return;
                      openEmailComposer({
                        template: 'recurrency_update',
                        to: '',
                        bcc: emails.join(','),
                        context: {
                          mode: 'bulk',
                          event: selectedEvent,
                          freshlingIds: freshlingsYesUnsure.map((f) => f.id),
                          registrants: freshlingsYesUnsure,
                        },
                      });
                    }}
                    className="recurrency-admin-print-btn recurrency-admin-email-all-btn"
                  >
                    Email all (Recurrency Update)
                  </button>
                  <button
                    type="button"
                    disabled={freshlingsNotResponded.length === 0}
                    onClick={() => {
                      if (freshlingsNotResponded.length === 0) return;
                      const emails = freshlingsNotResponded
                        .map((f) => (f.email || '').trim())
                        .filter(Boolean);
                      if (!emails.length) return;
                      openEmailComposer({
                        template: 'recurrency_update',
                        to: '',
                        bcc: emails.join(','),
                        context: {
                          mode: 'bulk',
                          event: selectedEvent,
                          freshlingIds: freshlingsNotResponded.map((f) => f.id),
                          registrants: freshlingsNotResponded,
                        },
                      });
                    }}
                    className="recurrency-admin-print-btn"
                    title={freshlingsNotResponded.length === 0 ? 'Everyone has responded' : `Send follow-up to ${freshlingsNotResponded.length} who haven't responded to the form`}
                  >
                    Send follow-up to non-responders ({freshlingsNotResponded.length})
                  </button>
                  <button
                    type="button"
                    disabled={freshlingsWithSlot.length === 0}
                    onClick={() => {
                      if (freshlingsWithSlot.length === 0) return;
                      const emails = freshlingsWithSlot
                        .map((f) => (f.email || '').trim())
                        .filter(Boolean);
                      if (!emails.length) return;
                      openEmailComposer({
                        template: 'recurrency_class_reminder',
                        to: '',
                        bcc: emails.join(','),
                        context: {
                          mode: 'reminder',
                          event: selectedEvent,
                          freshlingIds: freshlingsWithSlot.map((f) => f.id),
                          registrants: freshlingsWithSlot,
                        },
                      });
                    }}
                    className="recurrency-admin-print-btn"
                    title={freshlingsWithSlot.length === 0 ? 'No one has a scheduled class yet' : `Send class reminder to ${freshlingsWithSlot.length} with a scheduled slot`}
                  >
                    Send class reminder ({freshlingsWithSlot.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => openRecurrencyTableExport('print')}
                    disabled={!recurrencyExportRows.length}
                    className="recurrency-admin-print-btn"
                  >
                    Print table
                  </button>
                  <AdminCopyMenu
                    ariaLabel="Copy recurrency table"
                    disabled={!recurrencyExportRows.length}
                    items={[
                      {
                        label: 'Copy table…',
                        onSelect: () => openRecurrencyTableExport('copy'),
                        showCopiedFeedback: false,
                      },
                    ]}
                  />
                </div>
                <div className="recurrency-admin-table-wrap">
                  <table className="recurrency-admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Rec</th>
                        <th>Responded?</th>
                        <th>Form?</th>
                        <th>Reminder?</th>
                        <th>Last jump</th>
                        <th>Wed AM?</th>
                        <th>Notes</th>
                        <th>Slot</th>
                        <th>Jumps</th>
                        <th>Home DZ</th>
                        <th>Ht</th>
                        <th>Wt</th>
                        <th>Clear</th>
                      </tr>
                    </thead>
                    <tbody>
                      {freshlingsYesUnsure.map((f) => {
                        const ru = f.recurrencyUpdate;
                        const responded = !!ru;
                        const sentAt = f.recurrencyFormEmailSentAt;
                        const manual = !!f.recurrencyFormEmailManual;
                        const formSent = !!sentAt || manual;
                        const reminderSentAt = f.recurrencyReminderSentAt;
                        const reminderManual = !!f.recurrencyReminderManual;
                        const reminderSent = !!reminderSentAt || reminderManual;
                        return (
                          <tr key={f.id}>
                            <td>
                              <span className="recurrency-admin-name-cell">
                                {getFreshlingDisplayName(f) || f.name || f.email}
                                {f.email && (
                                  <button
                                    type="button"
                                    className={`recurrency-admin-email-icon ${formSent ? 'sent' : ''}`}
                                    title="Email recurrency update"
                                    onClick={() =>
                                      openEmailComposer({
                                        template: 'recurrency_update',
                                        to: f.email || '',
                                        bcc: '',
                                        context: {
                                          mode: 'single',
                                          flyer: f,
                                          event: selectedEvent,
                                          flyerId: f.id,
                                        },
                                      })
                                    }
                                  >
                                    ✉
                                  </button>
                                )}
                              </span>
                            </td>
                            <td>{f.phone || '—'}</td>
                            <td title={ru ? 'From form (or manually edited here)' : 'From import'}>
                              <select
                                value={f.recurrency ?? ''}
                                onChange={(e) => handleRecurrencyChange(f.id, e.target.value)}
                                className="recurrency-admin-rec-select"
                                aria-label="Recurrency status"
                              >
                                <option value="">—</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                                <option value="Unsure">Unsure</option>
                              </select>
                            </td>
                            <td>{responded ? 'Yes' : 'No'}</td>
                            <td>
                              <button
                                type="button"
                                className={`recurrency-admin-mark-sent ${formSent ? 'on' : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleRecurrencyEmailManual(f);
                                }}
                                disabled={markingSentId === f.id}
                                title={
                                  sentAt
                                    ? (() => {
                                        const t = sentAt;
                                        const d = t?.toDate ? t.toDate() : t;
                                        return d
                                          ? `Form email sent ${d.toLocaleDateString?.() ?? String(d)}`
                                          : 'Form email sent';
                                      })()
                                    : manual
                                      ? 'Marked sent (manual)'
                                      : 'Not sent'
                                }
                                aria-label={
                                  manual
                                    ? 'Unmark recurrency email sent'
                                    : 'Mark recurrency email sent manually'
                                }
                              >
                                ✓
                              </button>
                            </td>
                            <td>
                              <button
                                type="button"
                                className={`recurrency-admin-mark-sent ${reminderSent ? 'on' : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleRecurrencyReminderManual(f);
                                }}
                                disabled={markingReminderId === f.id}
                                title={
                                  reminderSentAt
                                    ? (() => {
                                        const t = reminderSentAt;
                                        const d = t?.toDate ? t.toDate() : t;
                                        return d
                                          ? `Class reminder sent ${d.toLocaleDateString?.() ?? String(d)}`
                                          : 'Class reminder sent';
                                      })()
                                    : reminderManual
                                      ? 'Marked sent (manual)'
                                      : 'Not sent'
                                }
                                aria-label={
                                  reminderManual
                                    ? 'Unmark class reminder sent'
                                    : 'Mark class reminder sent manually'
                                }
                              >
                                ✓
                              </button>
                            </td>
                            <td>{formatLastJumpDate(ru?.lastJumpDate)}</td>
                            <td>{ru?.availableWednesdayMorning || '—'}</td>
                            <td className="recurrency-admin-notes-cell" title={ru?.notes || ''}>
                              {ru?.notes ? (ru.notes.length > 120 ? ru.notes.slice(0, 120) + '…' : ru.notes) : '—'}
                            </td>
                            <td>{ru?.timeSlotId ? getSlotLabel(ru.timeSlotId) : '—'}</td>
                            <td>{f.jumpNumber != null && f.jumpNumber !== '' ? f.jumpNumber : '—'}</td>
                            <td>{f.homeDz || '—'}</td>
                            <td>{f.ht != null && f.ht !== '' ? formatHeight(String(f.ht)) : '—'}</td>
                            <td>{f.wt != null && f.wt !== '' ? formatWeight(String(f.wt)) : '—'}</td>
                            <td className="recurrency-admin-clear-cell">
                              {responded ? (
                                <button
                                  type="button"
                                  className="recurrency-admin-clear-form"
                                  onClick={() => clearFormData(f)}
                                  title="Clear form response and revert Rec to value when list was loaded"
                                >
                                  Clear
                                </button>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Print-only grouped list: classes then non-responders, larger font, alternating rows, includes phone */}
                <div className="recurrency-print-view" aria-hidden="true">
                  <p className="recurrency-print-view-title">{selectedEvent?.title || selectedEventId} — Recurrencies</p>
                  <p className="recurrency-print-view-counts">
                    <strong>Yes:</strong> {yesCount} &nbsp; <strong>Unsure:</strong> {unsureCount}
                  </p>
                  {printGrouped.filter((s) => s.flyers.length > 0).map((section) => (
                    <div key={section.slotId ?? 'non-responders'} className="recurrency-print-view-section">
                      <h4 className="recurrency-print-view-section-title">{section.label}</h4>
                      <table className="recurrency-print-view-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>Jumps</th>
                            <th>Ht</th>
                            <th>Wt</th>
                            <th>Home DZ</th>
                            <th>Last jump</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.flyers.map((f, idx) => {
                            const ru = f.recurrencyUpdate;
                            return (
                              <tr key={f.id} className={idx % 2 === 1 ? 'recurrency-print-view-row-alt' : ''}>
                                <td>{getFreshlingDisplayName(f) || f.name || f.email || '—'}</td>
                                <td>{f.phone || '—'}</td>
                                <td>{f.jumpNumber != null && f.jumpNumber !== '' ? f.jumpNumber : '—'}</td>
                                <td>{f.ht != null && f.ht !== '' ? formatHeight(String(f.ht)) : '—'}</td>
                                <td>{f.wt != null && f.wt !== '' ? formatWeight(String(f.wt)) : '—'}</td>
                                <td>{f.homeDz || '—'}</td>
                                <td>{formatLastJumpDate(ru?.lastJumpDate)}</td>
                                <td className="recurrency-print-view-notes">{ru?.notes || '—'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>

          <section className="recurrency-admin-section">
            <h3>Public form &amp; data</h3>
            <p>
              Share this link with participants who need to submit a recurrency update for <strong>{selectedEvent?.title || selectedEventId}</strong>. This link is for this event only; if you run multiple events, each has its own link.
            </p>
            <p>
              <a
                href={`${(process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '')}/recurrency-update?event=${encodeURIComponent(selectedEventId)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {(process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '')}/recurrency-update?event={selectedEventId}
              </a>
            </p>
            <p>
              View and edit recurrency status (Yes/No/Unsure) and submitted details in{' '}
              <Link to="/admin/flyer-management">Flyers</Link> — select this event and use the Rec and Rec update columns.
            </p>
          </section>
        </>
      )}
      {recurrencyTableExport && (
        <AdminColumnExportModal
          mode={recurrencyTableExport.mode}
          columns={RECURRENCY_EXPORT_COLS}
          selectedIds={recurrencyExportCols}
          onChangeSelected={setRecurrencyExportCols}
          onCancel={() => setRecurrencyTableExport(null)}
          onConfirm={runRecurrencyTableExport}
          onResetDefaults={resetRecurrencyExportCols}
        />
      )}

      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
        onEmailSent={handleEmailSent}
      />
    </div>
  );
};

export default RecurrencyAdmin;
